import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/sections/PageHero";
import { Services } from "@/components/sections/Services";
import { DetachReset } from "@/components/sections/DetachReset";
import { Troubleshoot } from "@/components/sections/Troubleshoot";
import { FinalCta } from "@/components/sections/FinalCta";
import { LeadForm } from "@/components/sections/LeadForm";
import { QuoteButton } from "@/components/site/Cta";
import { SERVICES } from "@/content/site";

const OPTION = { installation: "Solar Installation", "detach-reset": "Detach & Reset", maintenance: "Solar Maintenance", troubleshooting: "Troubleshooting", cleaning: "Panel Cleaning", electrical: "Electrical", batteries: "Solar Battery", "main-service": "Main Service Upgrade", "ev-chargers": "EV Charger", "soffit-lighting": "Soffit Lighting", "pest-guard": "Pest Guard" };

const ServiceDetails = () => (
  <section data-testid="service-details" className="bg-[#08090a] py-20 sm:py-28">
    <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
      {SERVICES.map((s, i) => (
        <motion.article key={s.id} id={`svc-${s.id}`} data-testid={`service-detail-${s.id}`}
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 0.8 }}
          className="group grid gap-6 border-t border-white/10 py-10 lg:grid-cols-12 lg:gap-10 lg:py-14">
          <span className="font-mono text-sm text-amber-400 lg:col-span-1">{String(i + 1).padStart(2, "0")}</span>
          <h2 className="text-2xl sm:text-3xl font-semibold uppercase text-white lg:col-span-4">{s.title}</h2>
          <div className="lg:col-span-5">
            <p className="text-base font-medium text-zinc-100">{s.text}</p>
            <p className="mt-3 text-base leading-relaxed text-zinc-300">{s.detail}</p>
          </div>
          <div className="flex flex-col items-start gap-3 lg:col-span-2 lg:items-end">
            <QuoteButton service={OPTION[s.id]} testId={`service-detail-quote-${s.id}`} className="!px-5 !py-3">Request Service</QuoteButton>
            {s.href.startsWith("/solar") && (
              <Link to={s.href} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-300 hover:text-amber-400">Full page <ArrowUpRight className="h-3.5 w-3.5" /></Link>
            )}
          </div>
        </motion.article>
      ))}
    </div>
  </section>
);

export default function ServicesPage() {
  return (
    <main data-testid="services-page">
      <Seo path="/services" title="Solar Services in Texas | Repair, Maintenance, Detach & Reset, Cleaning | Solar Energy Doc"
        description="Complete solar and energy services across Texas: installation, detach & reset, maintenance, troubleshooting and repair, panel cleaning, electrical services and solar batteries." />
      <PageHero eyebrow="Services · Texas" testId="services-page-hero" lines={["Complete solar &", "energy services."]}
        sub="From installation to troubleshooting, Solar Energy Doc keeps your energy system performing at its best." />
      <Services />
      <ServiceDetails />
      <DetachReset />
      <Troubleshoot />
      <FinalCta />
      <LeadForm />
    </main>
  );
}
